import { useEffect, useMemo, useState } from "react";
import { loadPlayers, type Player } from "@/lib/bonus";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = {
  value: string;
  onChange: (name: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /**
   * Optional team filter — when set, only players of this team appear in
   * the combobox. Used by the Austria-themed bonus questions so users
   * can't pick e.g. a Brazil striker as Austria's top scorer.
   * Matches against the API-Football team name (English, e.g. "Austria").
   */
  team?: string;
};

export function PlayerCombobox({ value, onChange, disabled, placeholder = "Spieler suchen …", team }: Props) {
  const [open, setOpen] = useState(false);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    loadPlayers().then(p => setPlayers(p)).finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const filtered = team ? players.filter((p) => p.team === team) : players;
    const by: Record<string, Player[]> = {};
    filtered.forEach(p => { (by[p.team] = by[p.team] ?? []).push(p); });
    return Object.keys(by).sort().map(team => ({ team, players: by[team] }));
  }, [players, team]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "w-full h-12 px-3 rounded-lg border border-input bg-background text-base font-semibold text-left flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        >
          <span className={cn("truncate", !value && "text-muted-foreground font-normal")}>
            {value || (loading ? "Lade Spieler …" : placeholder)}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-h-[60vh]" align="start">
        <Command>
          <CommandInput placeholder="Name oder Team suchen …" />
          <CommandList>
            <CommandEmpty>Kein Spieler gefunden.</CommandEmpty>
            {value && (
              <CommandGroup>
                <CommandItem
                  value={`__clear__`}
                  onSelect={() => { onChange(""); setOpen(false); }}
                  className="text-muted-foreground italic"
                >
                  — Auswahl entfernen —
                </CommandItem>
              </CommandGroup>
            )}
            {grouped.map(({ team, players }) => (
              <CommandGroup key={team} heading={team}>
                {players.map(p => (
                  <CommandItem
                    key={p.id}
                    value={`${p.name} ${p.team}`}
                    onSelect={() => { onChange(p.name); setOpen(false); }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", value === p.name ? "opacity-100" : "opacity-0")} />
                    <span className="flex-1 truncate">{p.name}</span>
                    {p.position && <span className="text-[10px] text-muted-foreground ml-2">{p.position}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
